import React, { useState, useEffect } from 'react';
import { Trash2, Search, MapPin, Package, DollarSign, Calendar, RefreshCw, RotateCcw, Clock, AlertTriangle, X } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDeletedJobs = () => {
  const { token, userType } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const isFullAdmin = userType === 'admin';

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const fetchDeletedJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/deleted-jobs`, getAuthHeader());
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch deleted jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeletedJobs(); }, []);

  const handleRestore = async (jobId, title) => {
    if (!window.confirm(`Restore "${title}" back to active jobs?`)) return;
    try {
      await axios.post(`${API}/admin/deleted-jobs/${jobId}/restore`, {}, getAuthHeader());
      toast.success(`"${title}" restored successfully`);
      fetchDeletedJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore job');
    }
  };

  const handlePermanentDelete = async (jobId, title) => {
    if (!window.confirm(`PERMANENTLY delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/deleted-jobs/${jobId}/permanent`, getAuthHeader());
      toast.success(`"${title}" permanently deleted`);
      fetchDeletedJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete');
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(search.toLowerCase()) ||
    job.client.toLowerCase().includes(search.toLowerCase()) ||
    (job.job_number && job.job_number.includes(search))
  );

  const getDaysRemaining = (deletedAt) => {
    if (!deletedAt) return null;
    const deleted = new Date(deletedAt);
    const purgeDate = new Date(deleted.getTime() + 120 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const days = Math.ceil((purgeDate - now) / (24 * 60 * 60 * 1000));
    return Math.max(0, days);
  };

  return (
    <div className="space-y-6" data-testid="deleted-jobs-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trash2 className="h-8 w-8 text-red-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deleted Jobs</h1>
            <p className="text-gray-500 text-sm">Soft-deleted jobs awaiting permanent removal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="text-sm" data-testid="deleted-count">
            {jobs.length} deleted jobs
          </Badge>
          <Button variant="outline" onClick={fetchDeletedJobs} className="flex items-center gap-2" data-testid="refresh-deleted">
            <RefreshCw size={18} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          placeholder="Search deleted jobs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-deleted"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full" />
        </div>
      ) : filteredJobs.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <Trash2 className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-1">No Deleted Jobs</h3>
          <p className="text-gray-400">
            {search ? 'No deleted jobs match your search' : 'Deleted jobs will appear here for 120 days before permanent removal'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredJobs.map(job => {
            const daysLeft = getDaysRemaining(job.deleted_at);
            return (
              <Card key={job.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`deleted-job-${job.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {job.job_number && <span className="text-blue-600 mr-1">#{job.job_number}</span>}
                        {job.title}
                      </h3>
                      <Badge className={
                        job.deletion_reason === 'auto_expired_archive'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                      }>
                        {job.deletion_reason === 'auto_expired_archive' ? 'Expired Archive' : 'Manually Deleted'}
                      </Badge>
                      {job.is_split_parent && <Badge className="bg-indigo-100 text-indigo-700">Parent</Badge>}
                      {job.is_team_job && <Badge className="bg-teal-100 text-teal-700">Team</Badge>}
                    </div>
                    <p className="text-sm text-gray-500">{job.client} {job.location && `• ${job.location}`}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Package size={14} />
                        {job.deliveries} {job.delivery_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign size={14} />
                        ${job.total_pay?.toFixed(2)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Deleted: {new Date(job.deleted_at).toLocaleDateString()}
                      </span>
                      {daysLeft !== null && (
                        <span className={`flex items-center gap-1 font-medium ${daysLeft <= 14 ? 'text-red-600' : daysLeft <= 30 ? 'text-amber-600' : 'text-gray-500'}`}>
                          <Clock size={14} />
                          {daysLeft} days until permanent deletion
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {isFullAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(job.id, job.title)}
                          className="text-blue-600 hover:bg-blue-50 border-blue-300"
                          data-testid={`restore-${job.id}`}
                        >
                          <RotateCcw size={16} className="mr-1" />
                          Restore
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePermanentDelete(job.id, job.title)}
                          className="text-red-600 hover:bg-red-50 border-red-300"
                          data-testid={`permanent-delete-${job.id}`}
                        >
                          <X size={16} className="mr-1" />
                          Purge
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="p-4 border-0 shadow-sm bg-red-50">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-red-800">Auto-Purge Policy</h4>
            <p className="text-red-600 text-sm">
              Deleted jobs are automatically and permanently removed after 120 days. 
              Archived jobs are moved here after 365 days in the archives.
              {isFullAdmin ? ' Administrators can restore or permanently purge jobs at any time.' : ''}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminDeletedJobs;
